import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import MatrixLoader from './MatrixLoader';
import { speak } from '../utils/voice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    phone: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);
  const [pendingLogin, setPendingLogin] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/admin-login`, {
        phone: credentials.phone,
        password: credentials.password
      });
      localStorage.setItem('ashop_admin', 'true');
      localStorage.setItem('ashop_admin_token', response.data.token);
      // Track session start for 8-hour expiry check
      localStorage.setItem('ashop_admin_login_time', String(Date.now()));
      toast.success('Welcome to Ashop Admin Portal!');
      // Fire voice welcome directly from user-click context (browsers require this for autoplay)
      speak('Welcome to admin portal.', { rate: 0.95, pitch: 1.05 });
      // Show matrix loader, then trigger onLogin when it finishes
      setPendingLogin(true);
      setShowMatrix(true);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error('Invalid credentials. Access denied.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showMatrix && (
        <MatrixLoader
          duration={5200}
          statusLines={[
            'Verifying credentials',
            'Establishing encrypted channel',
            'Loading admin dashboard',
            'Access granted.',
          ]}
          onDone={() => {
            setShowMatrix(false);
            if (pendingLogin) onLogin(true);
          }}
        />
      )}
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 p-4">
      {/* Back Button */}
      <div className="container mx-auto max-w-md mb-6 pt-4">
        <Link
          to="/"
          className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-semibold rounded-xl hover:from-blue-700 hover:to-blue-900 transition-all duration-300 shadow-lg hover:shadow-xl"
        >
          <i className="fas fa-arrow-left"></i>
          <span>Back</span>
        </Link>
      </div>

      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
            <i className="fas fa-shield-alt text-white text-3xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Admin Access</h1>
          <p className="text-gray-600">Secure login for Ashop administrators</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="fas fa-phone mr-2"></i>
              Phone Number
            </label>
            <input
              type="tel"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors"
              placeholder="Enter admin phone number"
              value={credentials.phone}
              onChange={(e) => setCredentials(prev => ({ ...prev, phone: e.target.value }))}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <i className="fas fa-lock mr-2"></i>
              Password
            </label>
            <input
              type="password"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:outline-none transition-colors"
              placeholder="Enter admin password"
              value={credentials.password}
              onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-bold rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <i className="fas fa-sign-in-alt"></i>
                <span>Access Admin Portal</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            <i className="fas fa-shield-check mr-1"></i>
            Authorized personnel only
          </p>
        </div>
      </div>
      </div>
    </div>
    </>
  );
};

export default AdminLogin;